import React, { useState, useEffect, useCallback } from 'react';
import TreeChart from './TreeChart';
import { fetchEquityData } from '../services/api';
import config from '../config';
import './EquityPenetration.css';
import { Spin, Alert, Button, Modal, Input, message } from 'antd';
import { ArrowLeftOutlined, SettingOutlined } from '@ant-design/icons';

// 本地存储的授权码 key
const AUTHORIZE_CODE_KEY = 'clan_authorize_code';

const EquityPenetration = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [title, setTitle] = useState('家族族谱');
    
    // 授权码相关状态
    const [authModalVisible, setAuthModalVisible] = useState(false);
    const [inputAuthorizeCode, setInputAuthorizeCode] = useState('');
    const [authModalLoading, setAuthModalLoading] = useState(false);
    const [hasInitialized, setHasInitialized] = useState(false);

    // 返回小程序
    const goBack = () => {
        const w = window;
        if (w.wx && w.wx.miniProgram) {
            w.wx.miniProgram.navigateBack();
        } else {
            w.history.back();
        }
    };

    // 从 localStorage 加载授权码
    const loadAuthorizeCodeFromStorage = () => {
        const savedCode = localStorage.getItem(AUTHORIZE_CODE_KEY);
        if (savedCode) {
            config.setAuthorizeCode(savedCode);
            setInputAuthorizeCode(savedCode);
        }
        return savedCode;
    };

    // 保存授权码到 localStorage 和 config
    const saveAuthorizeCode = (code) => {
        localStorage.setItem(AUTHORIZE_CODE_KEY, code);
        config.setAuthorizeCode(code);
    };

    // 加载数据的函数
    const loadData = useCallback(async (authorizeCode) => {
        try {
            setLoading(true);
            setError(null);

            // 解析小程序跳转携带的 URL 参数
            const params = new URLSearchParams(window.location.search);
            const urlStoreId = params.get('storeId');
            const urlTitle = params.get('title');

            // 设置标题
            if (urlTitle) {
                setTitle(decodeURIComponent(urlTitle));
            }
            const storeId = urlStoreId || '1847817655894917121';

            if (!storeId) {
                setError('参数 storeId 不能为空');
                setLoading(false);
                return;
            }
			//族谱图设置默认加载到哪个节点的
			const targetMemberCode=params.get('targetMemberCode') || '';
            
            const response = await fetchEquityData(storeId, authorizeCode,targetMemberCode);

            if (response.success) {
                // 检查是否有数据
                if (!response.data || (Array.isArray(response.data) && response.data.length === 0)) {
                    setError('无家族数据，请联系管理员！');
                    setData(null);
                } else {
                    setData(response.data);
                }
            } else {
                setError(response.error || '数据加载失败');
                // 如果是授权相关的错误，提示用户重新设置授权码
                if (response.error && (
                    response.error.includes('授权') || 
                    response.error.includes('认证') || 
                    response.error.includes('权限') ||
                    response.error.includes('无效') ||
                    response.error.includes('过期')
                )) {
                    message.warning('授权码可能无效，请重新设置');
                }
            }
        } catch (err) {
            setError('加载数据时发生错误');
            console.error('数据加载错误:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    // 处理授权码确认
    const handleAuthConfirm = async () => {
        if (!inputAuthorizeCode.trim()) {
            message.warning('请输入授权码');
            return;
        }
        
        setAuthModalLoading(true);
        const code = inputAuthorizeCode.trim();
        saveAuthorizeCode(code);
        
        // 尝试加载数据
        await loadData(code);
        
        setAuthModalLoading(false);
        setAuthModalVisible(false);
    };

    // 打开设置授权码弹框
    const openAuthModal = () => {
        const currentCode = config.getAuthorizeCode() || localStorage.getItem(AUTHORIZE_CODE_KEY) || '';
        setInputAuthorizeCode(currentCode);
        setAuthModalVisible(true);
    };

    // 初始化时检查授权码
    useEffect(() => {
        if (hasInitialized) return;
        
        const savedCode = loadAuthorizeCodeFromStorage();
        
        if (savedCode) {
            // 有保存的授权码，直接加载数据
            loadData(savedCode);
        } else {
            // 没有授权码，弹出设置弹框
            setAuthModalVisible(true);
        }
        
        setHasInitialized(true);
    }, [hasInitialized, loadData]);

    // 授权码弹框
    const renderAuthModal = () => (
        <Modal
            title="设置授权码"
            open={authModalVisible}
            onOk={handleAuthConfirm}
            onCancel={() => {
                // 如果没有授权码且用户取消，提示必须设置
                const savedCode = localStorage.getItem(AUTHORIZE_CODE_KEY);
                if (!savedCode) {
                    message.warning('请先设置授权码才能查看族谱数据');
                } else {
                    setAuthModalVisible(false);
                }
            }}
            confirmLoading={authModalLoading}
            okText="确定"
            cancelText="取消"
            maskClosable={false}
            closable={!!localStorage.getItem(AUTHORIZE_CODE_KEY)}
        >
            <div style={{ marginBottom: 12 }}>
                <p style={{ color: '#666', marginBottom: 8 }}>
                    请输入授权码以访问族谱数据：
                </p>
                <Input.Password
                    placeholder="请输入授权码"
                    value={inputAuthorizeCode}
                    onChange={(e) => setInputAuthorizeCode(e.target.value)}
                    onPressEnter={handleAuthConfirm}
                    autoFocus
                />
            </div>
        </Modal>
    );

    // 渲染加载状态
    if (loading && !data) {
        return (
            <div className="equity-penetration-container loading-container">
                {renderAuthModal()}
                <Spin tip="加载数据..." size="large" />
            </div>
        );
    }

    // 渲染错误状态
    if (error) {
        return (
            <div className="equity-penetration-container error-container">
                {renderAuthModal()}
                <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
                    <Button
                        type="default"
                        icon={<ArrowLeftOutlined />}
                        onClick={goBack}
                    >
                        返回
                    </Button>
                    <Button
                        type="primary"
                        icon={<SettingOutlined />}
                        onClick={openAuthModal}
                    >
                        重新设置授权码
                    </Button>
                </div>
                <Alert
                    message="提示"
                    description={error}
                    type="error"
                    showIcon
                />
            </div>
        );
    }

    // 渲染图表
    return (
        <div className="equity-penetration-container">
            {renderAuthModal()}
            <div className="chart-header">
                <div className="chart-title">{title}</div>
                <Button
                    type="text"
                    icon={<SettingOutlined />}
                    onClick={openAuthModal}
                    className="setting-btn"
                    title="设置授权码"
                />
            </div>
            {data && <TreeChart data={data} />}
        </div>
    );
};

export default EquityPenetration;
